import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import * as Api from "../lib/api";
import { useAuth } from "../lib/auth";
import { AuthShell, Field } from "./LoginPage";

function passwordIssues(pw, confirm) {
  const issues = [];
  if (pw.length < 8) issues.push("At least 8 characters");
  if (!/[A-Z]/.test(pw)) issues.push("One uppercase letter");
  if (!/[a-z]/.test(pw)) issues.push("One lowercase letter");
  if (!/[0-9]/.test(pw)) issues.push("One number");
  if (confirm && pw !== confirm) issues.push("Passwords must match");
  return issues;
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { loginAs } = useAuth();
  const nav = useNavigate();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navTimer = useRef(null);

  // Clear pending nav timer if the user navigates away mid-redirect.
  useEffect(() => () => { if (navTimer.current) clearTimeout(navTimer.current); }, []);

  const issues = useMemo(() => passwordIssues(pw, confirm), [pw, confirm]);
  const canSubmit = pw.length > 0 && confirm.length > 0 && issues.length === 0 && !!token;

  if (!token) {
    return (
      <AuthShell title="Reset Link Invalid" subtitle="">
        <div className="space-y-5" data-testid="reset-password-no-token">
          <p className="text-white/70 text-sm">This reset link is missing or malformed. Request a fresh one.</p>
          <Link to="/forgot-password"
            className="block w-full h-14 bg-[#F26522] text-black text-center leading-[3.5rem] font-heading font-black tracking-wider uppercase text-base hover:bg-[#FF7A2A] transition-colors">
            Request New Link
          </Link>
        </div>
      </AuthShell>
    );
  }

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const d = await Api.resetPassword(token, pw);
      loginAs(d.access_token, d.user, d.team);
      setDone(true);
      // brief pause so the user sees the success state, then drop them on the dashboard
      navTimer.current = setTimeout(() => nav("/"), 1400);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error("This reset link is invalid or has already been used.");
      } else if (status === 410) {
        toast.error("This reset link has expired. Request a new one.");
      } else if (status === 422) {
        toast.error("Password does not meet the minimum requirements.");
      } else {
        toast.error(err?.response?.data?.detail || "Could not reset password.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthShell title="Password Reset" subtitle="">
        <div className="space-y-5" data-testid="reset-password-success">
          <div className="bg-black border-2 border-[#10B981] p-5 flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-0.5" />
            <div className="text-white/90 text-sm leading-relaxed">
              Your password has been updated. Signing you in…
            </div>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set New Password" subtitle="Choose a strong one — at least 8 characters">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
        <Field
          label="New password"
          type="password"
          value={pw}
          onChange={setPw}
          testId="reset-password-new"
          required
          autoComplete="new-password"
        />
        <Field
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          testId="reset-password-confirm"
          required
          autoComplete="new-password"
        />

        {(pw.length > 0 || confirm.length > 0) && issues.length > 0 && (
          <ul className="space-y-1 pl-4 list-disc text-[11px] font-mono tracking-wider uppercase text-[#F26522]" data-testid="reset-password-issues">
            {issues.map(i => <li key={i}>{i}</li>)}
          </ul>
        )}

        <button
          type="submit"
          disabled={busy || !canSubmit}
          data-testid="reset-password-submit"
          className="w-full h-16 bg-[#F26522] text-black font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 hover:bg-[#FF7A2A] transition-colors disabled:opacity-60">
          {busy && <Loader2 className="w-5 h-5 animate-spin" />}
          Reset Password
        </button>

        <div className="text-center pt-4 border-t border-white/10">
          <Link to="/login" data-testid="reset-password-cancel" className="text-[#F26522] hover:underline font-bold text-sm">
            ← Back to log in
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
